import React, { useEffect, useState, useRef } from "react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_elegance-sings/artifacts/d7lixhb1_IMG_9768.PNG";

/**
 * AMALI brand mark.
 *
 * `variant="compact"` — small typographic gold AMALI for the navbar.
 *
 * `variant="full"` — the official logo artwork (gold notes, navy AMALI letters,
 * gray subtitle, script signature) with the cream paper background removed
 * client-side via a `<canvas>` chroma-key so the dark navy site shows through.
 * Original colours of every drawn element are preserved exactly.
 */
export const AmaliLogo = ({
  variant = "compact",
  size = 28,
  className = "",
  showSubtitle = true,
}) => {
  if (variant === "full") {
    return <ProcessedLogo className={className} />;
  }
  return (
    <div
      className={`inline-flex flex-col items-start leading-none ${className}`}
      data-testid="amali-logo"
    >
      <div
        className="font-serif gold-text-gradient"
        style={{
          fontSize: `${size}px`,
          fontWeight: 500,
          letterSpacing: `${Math.max(2, size * 0.18)}px`,
          lineHeight: 1,
        }}
      >
        AMALI
      </div>
      {showSubtitle && (
        <div
          className="text-cream-muted/80 tracking-[0.32em] uppercase mt-1.5 font-light"
          style={{ fontSize: `${Math.max(8, size * 0.28)}px` }}
        >
          obrt za glazbene usluge
        </div>
      )}
    </div>
  );
};

/**
 * Loads the source artwork into a canvas, keys out the cream paper background
 * (light + low-saturation pixels become transparent) and exposes the result
 * as an <img>. Original element colours are untouched.
 */
const ProcessedLogo = ({ className = "" }) => {
  const [processedSrc, setProcessedSrc] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      if (cancelledRef.current) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const avg = (r + g + b) / 3;
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const sat = max === 0 ? 0 : (max - min) / max;

          // Cream paper: very light + low saturation → fully transparent
          if (avg > 222 && sat < 0.12) {
            data[i + 3] = 0;
          } else if (avg > 195 && sat < 0.18) {
            // Soft edge / anti-aliased margins → partial transparency
            const t = (avg - 195) / 30; // 0..1 as we approach pure cream
            const reduction = Math.min(0.92, t * 0.95);
            data[i + 3] = Math.round(data[i + 3] * (1 - reduction));
          }
        }

        ctx.putImageData(imageData, 0, 0);
        setProcessedSrc(canvas.toDataURL("image/png"));
      } catch (err) {
        // Canvas is tainted (CORS denied) — fall back to original src
        // eslint-disable-next-line no-console
        console.warn("Logo canvas processing failed, using original:", err);
        setProcessedSrc(LOGO_URL);
        setUsingFallback(true);
      }
    };

    img.onerror = () => {
      if (cancelledRef.current) return;
      setProcessedSrc(LOGO_URL);
      setUsingFallback(true);
    };

    img.src = LOGO_URL;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  return (
    <div
      className={`relative flex justify-center w-full ${className}`}
      data-testid="amali-logo-full"
    >
      <div
        className="relative overflow-hidden"
        style={{
          width: "min(640px, 92vw)",
          aspectRatio: "2.3 / 1",
        }}
      >
        {processedSrc && (
          <img
            src={processedSrc}
            alt="AMALI — obrt za glazbene usluge, vl. Valeria Jacek"
            className="absolute left-0 right-0 w-full h-auto select-none"
            style={{
              top: "50%",
              transform: "translateY(-50%)",
              // If we fell back to the un-processed image, gently blend it
              mixBlendMode: usingFallback ? "lighten" : "normal",
              filter: usingFallback
                ? "invert(1) hue-rotate(180deg) saturate(1.3) brightness(0.95)"
                : "none",
            }}
            draggable={false}
          />
        )}
      </div>
    </div>
  );
};

export default AmaliLogo;
